import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import TokenIcon from '@mui/icons-material/Token';
import AppsIcon from '@mui/icons-material/Apps';
import {BaseLayout} from "components/Admin/BaseLayout/BaseLayout";
import {Login} from "pages/Login/Login";
import {Settings} from "pages/Settings/Settings";
import {DeleteUser} from "pages/Users/DeleteUser/DeleteUser";
import {UserCreate} from "pages/Users/UserCreate/UserCreate";
import {UserList} from "pages/Users/UserList/UserList";
import {UserView} from "pages/Users/UserView/UserView";
import {useQueryClient} from "@tanstack/react-query";
import {SecureRoutes} from "components/Admin/Secure/SecureRoutes/SecureRoutes";
import {SecureRoute} from "components/Admin/Secure/SecureRoutes/components/SecureRoute/SecureRoute";
import {Permissions} from "dto/user-dto";
import {useUser} from "hooks/useUser";
import {Navigate, Route} from "react-router";
import {UserFilter} from "./pages/Users/UserFilter/UserFilter";
import {UserEdit} from "./pages/Users/UserEdit/UserEdit";
import {SeasonCreate} from "./pages/Season/SeasonCreate/SeasonCreate";
import {SeasonList} from "./pages/Season/SeasonList/SeasonList";
import {SeasonView} from "./pages/Season/SeasonView/SeasonView";
import {SeasonEdit} from "./pages/Season/SeasonEdit/SeasonEdit";
import {CollectionList} from "./pages/Collections/CollectionList/CollectionList";
import {CollectionCreate} from "./pages/Collections/CollectionCreate/CollectionCreate";
import {CollectionView} from "./pages/Collections/CollectionView/CollectionView";
import {CollectionEdit} from "./pages/Collections/CollectionEdit/CollectionEdit";
import {CollectionDelete} from "./pages/Collections/CollectionDelete/CollectionDelete";
import {CapCreate} from "./pages/Cap/CapCreate/CapCreate";
import {CustomOutlet} from "./components/Admin/CustomOutlet/CustomOutlet";
import {CapView} from "./pages/Cap/CapView/CapView";
import {CapEdit} from "./pages/Cap/CapEdit/CapEdit";
import {CapDelete} from "./pages/Cap/CapDelete/CapDelete";

export const Routing = () => {
  const queryClient = useQueryClient();

  const {user} = useUser();

  return (
    <SecureRoutes>
      <Route path="login" element={<Login/>}/>
      <Route element={<BaseLayout/>}>
        <SecureRoute
          path="/"
          permissions={[
            Permissions.CREATE_USER,
            Permissions.CREATE_SEASON,
            Permissions.DELETE_USER,
          ]}
          element={<Navigate to="/users" replace/>}
        />
        <SecureRoute
          resource="users"
          onClick={() => {
            queryClient.invalidateQueries({queryKey: ["users"]});
          }}
          icon={<PersonOutlineIcon/>}
          showInNavbar
          permissions={[
            Permissions.CREATE_USER,
            Permissions.EDIT_USER,
            Permissions.DELETE_USER,
          ]}
          element={<UserList/>}
        >
          <SecureRoute path="create" element={<UserCreate/>}/>
          <Route path="filter" element={<UserFilter/>}/>
          <Route path=":userId" element={<UserView/>}>
            <SecureRoute
              path="edit"
              permissions={[Permissions.EDIT_USER]}
              element={<UserEdit/>}
            />
            <SecureRoute
              path="delete"
              permissions={[Permissions.DELETE_USER]}
              element={<DeleteUser/>}
            />
          </Route>
        </SecureRoute>
        <SecureRoute
          resource="seasons"
          onClick={() => {
            queryClient.invalidateQueries({queryKey: ["seasons"]});
          }}
          icon={<TokenIcon/>}
          showInNavbar
          permissions={[Permissions.CREATE_SEASON, Permissions.DELETE_SEASON, Permissions.EDIT_SEASON]}
          element={<SeasonList/>}
        >
          <SecureRoute
            path="create"
            permissions={[Permissions.CREATE_SEASON]}
            element={<SeasonCreate/>}
          />
          <Route path=":recordId" element={<SeasonView/>}>
            <SecureRoute
              path="edit"
              permissions={[Permissions.EDIT_SEASON]}
              element={<SeasonEdit/>}
            />
            <SecureRoute
              path="delete"
              permissions={[Permissions.DELETE_SEASON]}
              element={<DeleteUser/>}
            />
          </Route>
        </SecureRoute>
        <SecureRoute
          resource="collections"
          onClick={() => {
            queryClient.invalidateQueries({queryKey: ["seasons"]});
          }}
          icon={<AppsIcon/>}
          showInNavbar
          permissions={[Permissions.CREATE_SEASON, Permissions.DELETE_SEASON, Permissions.EDIT_SEASON]}
          element={<CollectionList/>}
        >
          <SecureRoute
            path="create"
            permissions={[Permissions.CREATE_SEASON]}
            element={<CollectionCreate/>}
          />
          <Route path=":collectionId" element={<CollectionView/>}>
            <SecureRoute
              path="edit"
              permissions={[Permissions.EDIT_SEASON]}
              element={<CollectionEdit/>}
            />
            <SecureRoute
              path="delete"
              permissions={[Permissions.DELETE_SEASON]}
              element={<CollectionDelete/>}
            />
            <SecureRoute
              resource="caps"
              element={<CustomOutlet/>}
            >
              <SecureRoute
                path="create"
                element={<CapCreate/>}
              />
              <Route path=":capId" element={<CapView/>}>
                <SecureRoute
                  path="edit"
                  permissions={[Permissions.EDIT_SEASON]}
                  element={<CapEdit/>}
                />
                <SecureRoute
                  path="delete"
                  permissions={[Permissions.EDIT_SEASON]}
                  element={<CapDelete/>}
                />
              </Route>
            </SecureRoute>
          </Route>
        </SecureRoute>
        <SecureRoute
          icon={<SettingsIcon/>}
          showInNavbar
          path="/settings"
          label="pages.settings.label"
          element={<Settings/>}
        />
        {!user ? (
          <Route path="*" element={<Navigate to="/login" replace/>}/>
        ) : null}
      </Route>
    </SecureRoutes>
  );
};
